import { useState, useEffect, useCallback } from 'react';
import { attendanceAPI, classesAPI, departmentsAPI, locationsAPI, readersAPI, rolesAPI, createAuthenticatedWebSocket } from '../services/api';
import { formatDateTime, getRoleBadgeColor, getDirectionColor, getRoleDisplayName } from '../utils/helpers';
import { Search, Filter, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  getEmploymentTypeOptions,
  getEmploymentTypeLabel,
  getWorkArrangementLabel,
  workArrangementOptions,
} from '../utils/employment';

const emptyFilters = {
  from_date: '',
  to_date: '',
  department_id: '',
  section_id: '',
  role: '',
  employment_type: '',
  work_arrangement: '',
  location_id: '',
  reader_id: '',
  direction: '',
  search: '',
};

const Attendance = () => {
  const { user, entityCategory, terminology } = useAuth();
  const employmentTypeOptions = getEmploymentTypeOptions(user?.entity?.code);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sections, setSections] = useState([]);
  const [locations, setLocations] = useState([]);
  const [readers, setReaders] = useState([]);
  const [liveConnected, setLiveConnected] = useState(false);

  const fetchLogs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = {
        ...filters,
        limit: 100,
        offset,
      };
      const res = await attendanceAPI.getLogs(params);
      setLogs(res.data.logs);
      setTotalLogs(Number(res.data.total ?? res.data.logs.length));
    } catch {
      toast.error('Failed to load attendance logs');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => {
    setOffset(0);
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    let socket;
    let reconnectTimer;
    let closed = false;

    const connect = () => {
      socket = createAuthenticatedWebSocket();
      socket.onopen = () => setLiveConnected(true);
      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data);
          if (event.type === 'NEW_SCAN') {
            fetchLogs({ silent: true });
          }
        } catch {
          // Ignore malformed messages; reconnect/next scan will refresh naturally.
        }
      };
      socket.onerror = () => setLiveConnected(false);
      socket.onclose = () => {
        setLiveConnected(false);
        if (!closed) reconnectTimer = setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) socket.close();
    };
  }, [fetchLogs]);

  useEffect(() => {
    Promise.all([
      departmentsAPI.getAll(),
      rolesAPI.getAll(),
      classesAPI.getAll(),
      locationsAPI.getAll(),
      readersAPI.getAll(),
    ])
      .then(([departmentsRes, rolesRes, sectionsRes, locationsRes, readersRes]) => {
        setDepartments(departmentsRes.data.departments);
        setRoles(rolesRes.data.roles);
        setSections(sectionsRes.data.classes);
        setLocations(locationsRes.data.locations);
        setReaders(readersRes.data.readers);
      })
      .catch(() => toast.error('Failed to load attendance filters'));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Attendance Logs</h1>
          <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600">
            <span className={`h-2 w-2 rounded-full ${liveConnected ? 'bg-green-500' : 'bg-orange-400'}`} />
            {liveConnected ? 'Live sync' : 'Reconnecting'}
          </div>
        </div>
        <p className="text-gray-500 text-sm mt-1">
          Showing {logs.length ? offset + 1 : 0}-{Math.min(offset + logs.length, totalLogs)} of {totalLogs} scan events
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-100 space-y-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, ID, email or tag..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-400" />
              <input
                type="date"
                value={filters.from_date}
                aria-label="From date"
                title="From date"
                onChange={(e) => setFilters((prev) => ({ ...prev, from_date: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <input
              type="date"
              value={filters.to_date}
              aria-label="To date"
              title="To date"
              onChange={(e) => setFilters((prev) => ({ ...prev, to_date: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <select
              value={filters.department_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, department_id: e.target.value, section_id: '' }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All departments</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </select>

            <select
              value={filters.section_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, section_id: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All teams</option>
              {sections
                .filter((section) => !filters.department_id || String(section.department_id) === String(filters.department_id))
                .map((section) => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
            </select>

            <select
              value={filters.role}
              onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.code}>{role.name}</option>
              ))}
            </select>

            <select
              value={filters.employment_type}
              onChange={(e) => setFilters((prev) => ({ ...prev, employment_type: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All employment types</option>
              {employmentTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>

            <select
              value={filters.work_arrangement}
              onChange={(e) => setFilters((prev) => ({ ...prev, work_arrangement: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All work modes</option>
              {workArrangementOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>

            <select
              value={filters.location_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, location_id: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All locations</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>

            <select
              value={filters.reader_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, reader_id: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All readers</option>
              {readers.map((reader) => (
                <option key={reader.id} value={reader.id}>{reader.name || reader.reader_uid}</option>
              ))}
            </select>

            <select
              value={filters.direction}
              onChange={(e) => setFilters((prev) => ({ ...prev, direction: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All directions</option>
              <option value="in">IN</option>
              <option value="out">OUT</option>
            </select>

            <button
              type="button"
              onClick={() => {
                setFilters(emptyFilters);
                setOffset(0);
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Reset Filters
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <p className="text-4xl mb-2">📋</p>
              <p>No records found for the selected filters</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Department</th>
                    <th className="text-left px-4 py-3">{terminology.group}</th>
                    <th className="text-left px-4 py-3">{terminology.area}</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Employment</th>
                  <th className="text-left px-4 py-3">Work Mode</th>
                  <th className="text-left px-4 py-3">Tag UID</th>
                  <th className="text-left px-4 py-3">Reader</th>
                  <th className="text-left px-4 py-3">Checkpoint</th>
                  <th className="text-left px-4 py-3">Location</th>
                  <th className="text-left px-4 py-3">GPS Evidence</th>
                  <th className="text-left px-4 py-3">Direction</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Late</th>
                  <th className="text-left px-4 py-3">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{log.full_name}</td>
                    <td className="px-4 py-3 text-gray-500">{log.department_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{log.section_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{log.subject_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(log.role)}`}>
                        {getRoleDisplayName(log.role, entityCategory)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{getEmploymentTypeLabel(log.employment_type)}</td>
                    <td className="px-4 py-3 text-gray-500">{getWorkArrangementLabel(log.work_arrangement)}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{log.tag_uid}</td>
                    <td className="px-4 py-3 text-gray-500">{log.reader_name || log.reader_uid || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {log.checkpoint_type === 'bus' ? 'Bus' : log.checkpoint_type === 'main_gate' ? 'Main Gate' : log.checkpoint_type || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{log.location_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {log.mobile_latitude != null && log.mobile_longitude != null ? (
                        <div className="min-w-[12rem] space-y-1 text-xs">
                          <a
                            href={`https://www.google.com/maps?q=${log.mobile_latitude},${log.mobile_longitude}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            title="Open captured attendance location in Google Maps"
                          >
                            <MapPin size={14} />
                            {Number(log.mobile_latitude).toFixed(5)}, {Number(log.mobile_longitude).toFixed(5)}
                          </a>
                          <p>
                            Accuracy: {log.mobile_accuracy_metres != null
                              ? `±${Math.round(Number(log.mobile_accuracy_metres))} m`
                              : 'unknown'}
                            {log.mobile_location_provider ? ` · ${log.mobile_location_provider}` : ''}
                          </p>
                          {log.mobile_location_captured_at && (
                            <p>Captured: {formatDateTime(log.mobile_location_captured_at)}</p>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDirectionColor(log.direction)}`}>
                        {log.direction?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{log.status || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{log.late_minutes ? `${log.late_minutes} min` : '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDateTime(log.scan_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {totalLogs > 100 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset((current) => Math.max(current - 100, 0))}
              className="rounded-lg border border-gray-300 px-4 py-2 font-medium disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <span>Page {Math.floor(offset / 100) + 1} of {Math.ceil(totalLogs / 100)}</span>
            <button
              type="button"
              disabled={offset + logs.length >= totalLogs}
              onClick={() => setOffset((current) => current + 100)}
              className="rounded-lg border border-gray-300 px-4 py-2 font-medium disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Attendance;
